/**
 * Trading domain types and UI-specific helpers.
 */

import { AssetType, BetLifecycle, type PairMessage, type PairSchedule, type TradingPeriodMessage } from "@cronika/proto/common/trading_pb";

export const PAIR_CATEGORY = {
  CRYPTO: AssetType.CRYPTO,
  FOREX: AssetType.FOREX,
  STOCK: AssetType.STOCK,
  INDEX: AssetType.INDEX,
  COMMODITY: AssetType.COMMODITY,
  FAVORITE: "favorites",
} as const;

export type TPairCategory = (typeof PAIR_CATEGORY)[keyof typeof PAIR_CATEGORY];
export const OPEN_BET_LIFECYCLES: readonly BetLifecycle[] = [
  BetLifecycle.CREATED,
  BetLifecycle.ACCEPTED,
  BetLifecycle.SCHEDULED,
];

const toNumber = (value: string | number | null | undefined): number => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const isBetOpen = (lifecycle?: BetLifecycle | string | null): boolean =>
  Boolean(lifecycle && OPEN_BET_LIFECYCLES.includes(lifecycle as BetLifecycle));

export const getBetPeriodId = (setting: TradingPeriodMessage): string => {
  if (!setting) return "";
  return setting.id ?? String(getBetPeriodSeconds(setting));
};

export const getBetPeriodSeconds = (setting?: TradingPeriodMessage | null): number => {
  if (!setting) return 0;
  return toNumber(setting.ttl);
};

export const getBetPeriodLabel = (setting?: TradingPeriodMessage | null): string => {
  const time = getBetPeriodSeconds(setting);
  if (time >= 3600) return "H";
  if (time >= 60) return "M";
  return "S";
};

export const getBetPeriodPayoutPercent = (setting?: TradingPeriodMessage | null): number => {
  return toNumber(setting?.winPercentage);
};

export const getBetPeriodMinRaisePoints = (setting?: TradingPeriodMessage | null): number => {
  return toNumber(setting?.minRaisePoints);
};

export const getPairBetPeriods = (pair?: PairMessage | null): TradingPeriodMessage[] => {
  const periods = (pair?.betSettings ?? [])
    .filter((setting) => getBetPeriodSeconds(setting) > 0)
    .sort((a, b) => getBetPeriodSeconds(a) - getBetPeriodSeconds(b));

  const unique = new Map<string, TradingPeriodMessage>();
  periods.forEach((period) => {
    const id = getBetPeriodId(period);
    if (!unique.has(id)) unique.set(id, period);
  });

  return Array.from(unique.values());
};

export const getPairBasePayoutPercent = (pair?: PairMessage | null): number => {
  const setting = pair?.betSettings?.[0];
  if (setting) return getBetPeriodPayoutPercent(setting);
  return pair?.payoutPercent ? Math.round(Number(pair.payoutPercent)) : 0;
};

export const getPairMaxPayoutPercent = (pair?: PairMessage | null): number => {
  const periods = getPairBetPeriods(pair);
  if (periods.length) return Math.max(...periods.map(getBetPeriodPayoutPercent));
  return pair?.payoutPercent ? Math.round(Number(pair.payoutPercent)) : 0;
};

const UTC_DAY_NAMES = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

function parseHHMMtoSeconds(time: string): number {
  const parts = time.split(":");
  return (parseInt(parts[0] ?? "0", 10) * 3600) + (parseInt(parts[1] ?? "0", 10) * 60) + (parseInt(parts[2] ?? "0", 10));
}

/**
 * Returns seconds from now until the current trading session closes.
 * Returns null if no schedule is set (market always open).
 * Returns 0 if the market is currently closed (holiday, disabled day, or outside segments).
 */
export function getSecondsUntilMarketClose(schedule?: PairSchedule | null): number | null {
  if (!schedule) return null;

  const now = new Date();
  const todayUTC = now.toISOString().slice(0, 10);

  if (schedule.holidays.some((h) => h.date === todayUTC)) return 0;

  const dayName = UTC_DAY_NAMES[now.getUTCDay()];
  if (!dayName) return null;

  if (!(dayName in schedule.weekly)) return null;

  const daySchedule = schedule.weekly[dayName];
  if (!daySchedule || !daySchedule.enabled) return 0;

  const nowSecs = now.getUTCHours() * 3600 + now.getUTCMinutes() * 60 + now.getUTCSeconds();

  for (const seg of daySchedule.segments) {
    const openSecs = parseHHMMtoSeconds(seg.open);
    const closeSecs = parseHHMMtoSeconds(seg.close);
    if (nowSecs >= openSecs && nowSecs < closeSecs) {
      return closeSecs - nowSecs;
    }
  }

  return 0;
}
